// The fountain is laid out at a fixed pixel size and scaled down to fit the
// viewport. Fountain.css tried to express that as
//
//   transform: scale(min(1, calc(100vw / 1000px), calc(100vh / 1000px)))
//
// which is invalid: CSS calc() cannot divide a length by a length, so the whole
// declaration was dropped. postcss reports it at build time ("Cannot divide by
// px, number expected") and passes it through unchanged, so the fountain has
// never actually been scaled -- on any viewport shorter or narrower than its
// design size it simply overflowed.
//
// The ratio has to be computed where lengths are numbers, so it is computed
// here and published as the --fountain-scale custom property.

// Design size of the fountain, in px. The mobile layout draws it larger
// relative to the viewport, which is why its basis is smaller.
export const FOUNTAIN_BASIS = 1000;
export const FOUNTAIN_BASIS_MOBILE = 820;
export const MOBILE_MAX_WIDTH = 768; // matches the @media rule in Fountain.css

/**
 * Scale factor for a viewport, never above 1: the fountain shrinks to fit but
 * is never blown up past its design size.
 */
export function fountainScale(width, height, basis) {
  // A zero or missing dimension (a hidden tab, jsdom before layout) would give
  // 0 and collapse the fountain entirely. Falling back to 1 leaves it at its
  // design size until a real measurement arrives.
  if (!(width > 0) || !(height > 0) || !(basis > 0)) return 1;
  return Math.min(1, width / basis, height / basis);
}

export function basisFor(width) {
  return width <= MOBILE_MAX_WIDTH ? FOUNTAIN_BASIS_MOBILE : FOUNTAIN_BASIS;
}

/**
 * Measure the viewport and publish the scale. Uses visualViewport height where
 * available so the value tracks the *visible* area as mobile browser chrome
 * collapses, matching the 100dvh the layout now uses.
 */
export function applyFountainScale(win = window) {
  const width = win.innerWidth;
  const height = (win.visualViewport && win.visualViewport.height) || win.innerHeight;
  const scale = fountainScale(width, height, basisFor(width));
  win.document.documentElement.style.setProperty('--fountain-scale', String(scale));
  return scale;
}
